/**
 * MapFilterChips - Mobile
 * 
 * Horizontal scrollable filter chips for map filtering by opportunity status.
 * Renders in header area, not floating on map.
 * Uses design tokens exclusively per Cursor Rules.
 */

import React from 'react';
import { View, Text, ScrollView, Pressable, StyleSheet } from 'react-native';
import { color, text, space, radius, border, touch } from '../../../shared/tokens/mobile';
import { OpportunityStatus } from '../../../shared/types/opportunity';

export type MapFilter = 'all' | OpportunityStatus;

interface FilterOption {
  id: MapFilter;
  label: string;
  shortLabel?: string;
  statusColor: string;
}

const FILTER_OPTIONS: FilterOption[] = [
  { id: 'all', label: 'All', statusColor: color.textMuted },
  { id: 'evaluating', label: 'Evaluating', shortLabel: 'Eval', statusColor: color.info },
  { id: 'under_contract', label: 'Under Contract', shortLabel: 'Contract', statusColor: color.warning },
  { id: 'owned', label: 'Owned', statusColor: color.success },
  { id: 'selling', label: 'Selling', statusColor: color.warning },
  { id: 'passed', label: 'Passed', statusColor: color.disabled },
];

interface MapFilterChipsProps {
  activeFilter: MapFilter;
  onFilterPress: (filter: MapFilter) => void;
  counts?: Partial<Record<MapFilter, number>>;
}

export function MapFilterChips({ activeFilter, onFilterPress, counts }: MapFilterChipsProps) {
  // Filter to only show options with counts (always show 'all')
  const visibleOptions = FILTER_OPTIONS.filter(
    (option) => (counts?.[option.id] ?? 0) > 0 || option.id === 'all'
  );

  return (
    <View style={styles.container}>
      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.content}
      >
        {visibleOptions.map((filter) => {
          const isActive = activeFilter === filter.id;
          const count = counts?.[filter.id] ?? 0;
          const displayLabel = filter.shortLabel || filter.label;

          return (
            <Pressable
              key={filter.id}
              onPress={() => onFilterPress(filter.id)}
              style={({ pressed }) => [
                styles.chip,
                isActive && styles.chipActive,
                pressed && styles.chipPressed,
              ]}
              accessibilityRole="button"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={`${filter.label}, ${count} ${count === 1 ? 'opportunity' : 'opportunities'}`}
            >
              {/* Status indicator dot */}
              {filter.id !== 'all' && (
                <View 
                  style={[
                    styles.statusDot, 
                    { backgroundColor: filter.statusColor },
                  ]} 
                />
              )}

              {/* Label */}
              <Text style={[styles.chipText, isActive && styles.chipTextActive]}>
                {displayLabel}
              </Text>

              {/* Count badge */}
              {count > 0 && (
                <View style={[styles.countBadge, isActive && styles.countBadgeActive]}>
                  <Text style={[styles.countText, isActive && styles.countTextActive]}>
                    {count}
                  </Text>
                </View>
              )}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

// Legacy export for backward compatibility
export type FilterStatus = MapFilter;
export const FilterChips = MapFilterChips;

const styles = StyleSheet.create({
  container: {
    backgroundColor: color.background,
    borderBottomWidth: 1,
    borderBottomColor: color.border,
  },
  content: {
    paddingHorizontal: space.md,
    paddingVertical: space.sm,
    gap: space.sm,
    alignItems: 'center',
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: touch.minimum - 8,
    paddingVertical: space.xs,
    paddingHorizontal: space.sm,
    backgroundColor: color.surface,
    borderRadius: radius.full,
    borderWidth: border.width.thin,
    borderColor: color.border,
    gap: space.xs,
  },
  chipActive: {
    backgroundColor: color.primaryLight,
    borderColor: color.primary,
  },
  chipPressed: {
    opacity: 0.85,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  chipText: {
    fontSize: text.caption.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  chipTextActive: {
    color: color.primary,
    fontWeight: '600',
  },
  countBadge: {
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: color.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: space.xs,
  },
  countBadgeActive: {
    backgroundColor: color.primary,
  },
  countText: {
    fontSize: text.tabLabel.fontSize,
    fontWeight: '600',
    color: color.textSecondary,
  },
  countTextActive: {
    color: color.textInverse,
  },
});
